// src/services/dropboxService.js
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setIsLoading } from '../app/Actions/cmsAction';

// Load Dropbox API script
const loadDropboxScript = () => {
  return new Promise((resolve) => {
    if (window.Dropbox) {
      resolve();
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.id = 'dropboxjs';
    script.setAttribute('data-app-key', 'kyfo3zu6e4li7tz'); // Replace with your Dropbox app key
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
};

// Custom hook for Dropbox picker
export const useDropboxPicker = () => {
  const dispatch = useDispatch();
  
  const openPicker = useCallback(async ({ onSelect }) => {
    try {
      await loadDropboxScript();
      dispatch(setIsLoading(true));
      
      const options = {
        success: (files) => {
          if (files && files.length > 0) {
            onSelect(files);
          }
          dispatch(setIsLoading(false));
        },
        cancel: () => {
          console.log('User canceled the picker');
          dispatch(setIsLoading(false));
        },
        linkType: 'direct',
        multiselect: false,
        folderselect: false,
        extensions: ['.mp4', '.mov', '.avi', '.webm', '.mkv']
      };
      
      window.Dropbox.choose(options);
    } catch (error) {
      console.error('Error initializing Dropbox picker:', error);
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);
  
  return { openPicker };
};

// Download file from Dropbox
export const downloadFileFromDropbox = async (fileInfo) => {
  try {
    const response = await fetch(fileInfo.link);
    
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    // Create a File object from the blob
    return new File([blob], fileInfo.name, {
      type: getVideoMimeType(fileInfo.name),
      lastModified: new Date().getTime()
    });
  } catch (error) {
    console.error('Error downloading file from Dropbox:', error);
    throw error;
  }
};

// Helper function to determine MIME type from file extension
const getVideoMimeType = (filename) => {
  const extension = filename.split('.').pop().toLowerCase();
  const mimeTypes = {
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'webm': 'video/webm',
    'mkv': 'video/x-matroska'
  };
  
  return mimeTypes[extension] || 'video/mp4'; // Default to mp4 if unknown
};